/**
 * Application Initialization Module
 * Handles configuration loading, font initialization, and component setup
 */

export class AppInitializer {
  constructor(app) {
    this.app = app;
  }

  async initialize() {
    try {
      window.olivetoApp = this.app;

      this.setupWebViewCompatibility();
      this.showLoadingScreen();

      if (!this.app.configManager.hasValidRestaurantId()) {
        this.app.noRestaurantScreen.show();
        return;
      }

      await this.initializeConfiguration();
      await this.initializeFonts();

      this.initializeComponents();

      this.app.router.onNavigate = (viewName, data) => {
        this.app.handleViewChange(viewName, data);
      };

      if (this.app.deviceManager.isAndroid()) {
        this.app.deviceManager.logEvent('app_initialized', {
          deviceId: this.app.deviceManager.getDeviceId(),
          timestamp: new Date().toISOString()
        });
      }

      this.app.router.registerView('welcome', this.app.welcomeScreen);
      this.app.router.registerView('menu', this.app.menuView);

      await this.render();

      this.checkForPendingPayments();

      if (this.app.deviceManager.isAndroid() || document.body.classList.contains('webview-mode')) {
        setTimeout(() => {
          this.forceWebViewLayout();
        }, 300);
      }

    } catch (error) {
      console.error('Failed to initialize app:', error);
      this.showErrorScreen(error);
    }
  }

  async initializeConfiguration() {
    const savedRestaurantId = this.app.configManager.getSavedRestaurantSelection();
    if (savedRestaurantId) {
      this.app.configManager.currentRestaurantId = savedRestaurantId;
    }

    const config = await this.app.configManager.initialize();

    this.app.configManager.applyBranding();

    this.app.configManager.addConfigChangeListener((config, restaurantId) => {
      this.app.onConfigurationChange(config, restaurantId);
    });

    return config;
  }

  async initializeFonts() {
    try {
      const restaurantId = this.app.configManager.getCurrentRestaurantId();

      if (restaurantId) {
        await this.app.restaurantConfigService.loadConfiguration(restaurantId);

        this.showFontLoadingState();

        setTimeout(() => {
          this.hideFontLoadingState();
        }, 500);
      }
    } catch (error) {
      console.warn('Failed to load font configuration, using fallback fonts:', error);
      this.hideFontLoadingState();
    }
  }

  showFontLoadingState() {
    document.body.classList.add('fonts-loading');
  }

  hideFontLoadingState() {
    document.body.classList.remove('fonts-loading');
  }

  initializeComponents() {
    const config = this.app.configManager.getCurrentConfig();
    const menuData = this.app.configManager.getMenuData();

    this.app.welcomeScreen = this.app.createWelcomeScreen(config);
    this.app.menuView = this.app.createMenuView(config, menuData);
    this.app.orderConfirmationModal = this.app.createOrderConfirmationModal(config);
    this.app.orderSuccessModal = this.app.createOrderSuccessModal(config);

    const timing = (config?.paymentFlowConfiguration?.paymentTiming || '').toString();
    const isBeforeOrder = timing === 'BEFORE_ORDER' || timing === 'before_order';
    this.app.unifiedPaymentModal = this.app.createUnifiedPaymentModal(isBeforeOrder);
  }

  async render() {
    const app = document.getElementById('app');

    const restaurantSelectorHTML = await this.app.restaurantSelector.render();
    const appHTML = `
      ${restaurantSelectorHTML}
      ${this.app.welcomeScreen.render()}
      ${this.app.menuView.render()}
    `;

    app.innerHTML = appHTML;

    this.showWelcomeScreen();
  }

  showLoadingScreen() {
    const app = document.getElementById('app');
    app.innerHTML = `
      <div class="loading-screen">
        <div class="loading-content">
          <div class="loading-spinner"></div>
          <h2>Loading Restaurant Configuration...</h2>
          <p>Fetching menu data and settings</p>
        </div>
      </div>
    `;
  }

  showErrorScreen(error) {
    const app = document.getElementById('app');
    app.innerHTML = `
      <div class="error-screen">
        <div class="error-content">
          <div class="error-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <h2>Service Unavailable</h2>
          <p>We're unable to load the restaurant configuration at this time.</p>
          <p class="error-message">This may be due to a temporary service interruption or network connectivity issue.</p>
          <div class="error-actions">
            <button class="retry-btn" onclick="location.reload()">
              <i class="fas fa-redo"></i> Try Again
            </button>
          </div>
          <div class="contact-info">
            <p><strong>Need assistance?</strong></p>
            <p>Please contact technical support:</p>
            <p><i class="fas fa-envelope"></i> support@tabbly.com</p>
            <p><i class="fas fa-phone"></i> +1 (555) 123-4567</p>
          </div>
          <details class="error-details">
            <summary>Technical Details</summary>
            <pre>${error.message}</pre>
          </details>
        </div>
      </div>
    `;
  }

  showWelcomeScreen() {
    const menuContainer = document.querySelector('.main-container');
    const welcomeContainer = document.querySelector('.welcome-container');

    if (menuContainer) {
      menuContainer.style.display = 'none';
      menuContainer.style.visibility = 'hidden';
    }

    if (welcomeContainer) {
      welcomeContainer.style.display = 'flex';
      welcomeContainer.style.visibility = 'visible';
      welcomeContainer.style.opacity = '1';
      welcomeContainer.style.height = '100vh';
      welcomeContainer.style.minHeight = '100vh';
      welcomeContainer.style.position = 'fixed';
      welcomeContainer.style.top = '0';
      welcomeContainer.style.left = '0';
      welcomeContainer.style.width = '100%';
      welcomeContainer.style.zIndex = '1000';

      this.addWelcomeAnimations();
    }
  }

  addWelcomeAnimations() {
    const elements = [
      '.welcome-container .logo-container',
      '.tagline',
      '.buttons-container',
      '.specials-section',
      '.scroll-hint'
    ];

    elements.forEach((selector, index) => {
      const element = document.querySelector(selector);
      if (element) {
        element.style.opacity = '0';
        element.style.transform = 'translateY(30px)';
        element.style.transition = 'all 0.6s ease';

        setTimeout(() => {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0)';
        }, index * 200 + 500);
      }
    });

    const featuredDish = document.querySelector('.featured-dish');
    if (featuredDish) {
      featuredDish.style.opacity = '0';
      featuredDish.style.transform = 'scale(0.8) rotateY(20deg)';
      featuredDish.style.transition = 'all 1s ease';

      setTimeout(() => {
        featuredDish.style.opacity = '1';
        featuredDish.style.transform = 'scale(1) rotateY(0)';
      }, 1000);
    }
  }

  checkForPendingPayments() {
    console.log('[MainApp] Checking for pending payments on menu load...');

    const config = this.app.configManager.currentConfig;
    const paymentFlowConfig = config?.paymentFlowConfiguration;

    console.log('[MainApp] Payment flow configuration:', paymentFlowConfig);

    const requiresPayment = paymentFlowConfig?.paymentTiming === 'AFTER_ORDER' || paymentFlowConfig?.paymentTiming === 'after_order';
    const isBeforeOrder = paymentFlowConfig?.paymentTiming === 'BEFORE_ORDER' || paymentFlowConfig?.paymentTiming === 'before_order';
    const notificationConfig = paymentFlowConfig?.topbarNotification;

    const pendingOrder = this.app.cartManager.getPendingOrder();
    const orderState = this.app.cartManager.getOrderState();

    console.log('[MainApp] Payment check:', {
      requiresPayment,
      notificationEnabled: notificationConfig?.enabled,
      hasPendingOrder: !!pendingOrder,
      orderState,
      orderId: pendingOrder?.id
    });

    const shouldShowNotification = (pendingOrder && orderState === 'pending-payment') ||
                                  (requiresPayment && notificationConfig?.enabled !== false) ||
                                  (isBeforeOrder);

    if (shouldShowNotification) {
      console.log('[MainApp] Showing payment notification based on configuration');

      if (pendingOrder) {
        this.app.currentOrder = pendingOrder;
      }

      let message = pendingOrder
        ? (notificationConfig?.messages?.pendingMessage || 'Payment Required')
        : (isBeforeOrder
            ? (notificationConfig?.messages?.defaultMessage || 'Online Payment Required')
            : (notificationConfig?.messages?.defaultMessage || 'Payment Required'));

      if (isBeforeOrder) {
        message = 'Online Payment Required';
      } else if (pendingOrder) {
        message = 'Pay online, at the bar, or with staff';
      }
      if (/Payment Online Only Required/i.test(message)) {
        message = 'Online Payment Required';
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          this.app.showPaymentNotification(message, 'required');
        }, 100);
      });
    }
  }

  setupWebViewCompatibility() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isWebView = /wv|WebView|Android.*Version\/\d+\.\d+/.test(userAgent) ||
                      window.AndroidDevice !== undefined ||
                      this.app.deviceManager.isAndroid() ||
                      window.webkit?.messageHandlers !== undefined;

    if (isWebView) {
      document.documentElement.setAttribute('data-webview', 'true');

      document.documentElement.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; height: 100vh !important; width: 100vw !important;';

      document.body.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; background: white !important; margin: 0 !important; padding: 0 !important;';

      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content',
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        );
      }

      document.body.classList.add('webview-mode');

      setTimeout(() => {
        this.forceWebViewLayout();
      }, 100);
    }
  }

  forceWebViewLayout() {
    const welcomeScreenContainer = document.getElementById('welcome-screen-container');
    const menuScreenContainer = document.getElementById('menu-screen-container');

    if (welcomeScreenContainer) {
      welcomeScreenContainer.style.display = 'block';
      welcomeScreenContainer.style.visibility = 'visible';
      welcomeScreenContainer.style.height = '100vh';
      welcomeScreenContainer.style.minHeight = '100vh';
    }

    if (menuScreenContainer) {
      menuScreenContainer.style.display = 'none';
      menuScreenContainer.style.visibility = 'hidden';
    }

    document.documentElement.offsetHeight;
  }
}
